import { Avatar as MUIAvatar, type AvatarProps as MUIAvatarProps } from "@mui/material";
import { useState } from "react";

interface AvatarProps extends MUIAvatarProps {
  name: string;
  src?: string;
}

function getInitials(name: string): string {
  const [firstName, lastName] = name.split(" ");
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
}

function stringToColor(string: string) {
  let hash = 0;
  let index;

  /* eslint-disable no-bitwise */
  for (index = 0; index < string.length; index += 1) {
    // Convert each character to its ASCII code and add to the hash
    // The left shift (<<) and subtraction create a unique hash for the string
    // eslint-disable-next-line unicorn/prefer-code-point
    hash = string.charCodeAt(index) + ((hash << 5) - hash);
  }

  let color = "#";

  for (index = 0; index < 3; index += 1) {
    // Extract each color component (R, G, B) from the hash
    // Right shift (>>) by 0, 8, or 16 bits, then mask with 0xff to get values 0-255
    const value = (hash >> (index * 8)) & 0xff;
    // Convert to hex and ensure it's two digits
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name: string) {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: getInitials(name),
  };
}

export function Avatar(props: AvatarProps) {
  const { name, src, sx, ...avatarProps } = props;
  const [imageError, setImageError] = useState(false);

  if (!src || imageError) {
    return <MUIAvatar {...avatarProps} {...stringAvatar(name)} sx={sx} />;
  }

  return (
    <MUIAvatar
      {...avatarProps}
      src={src}
      alt={name}
      sx={{
        backgroundColor: (theme) => theme.palette.primary.main,
        color: (theme) => theme.palette.primary.contrastText,
        // oxlint-disable-next-line typescript/no-misused-spread
        ...sx,
      }}
      onError={() => {
        setImageError(true);
      }}
    />
  );
}
